/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { css } from '@emotion/core'
import {
  Card,
  colors,
  BillboardButton,
  useOverlay,
  BillboardText,
  Divider,
  IconButton,
  TextField,
  H2,
  Separator,
  Paragraph,
  Alert,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { DndContext, rectIntersection, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ContentAdd, ControlDrag } from '@fs/zion-icon'
import cloneDeep from 'lodash.clonedeep'
import debounce from 'lodash.debounce'
import { expandedNameFieldsSortMap, sortPersonNameSubElements, useSlsHighlightsContext } from '@fs/zion-sls'
import SortableItem from '../../../indexing/components/ui/SortableItem'
import Droppable from '../../../indexing/components/ui/Droppable'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import { filterIllegalReviewFieldCharacters, getNextUuid } from '../../../../../shared/utils/utils'
import useTreatedNameFieldChips, {
  disableSaveFuncRequireGnOrSurn,
} from '../../../indexing/hooks/useTreatedNameFieldChips'
import { useFullNameDispatchContext, useFullNameStateContext } from '../../providers/FullNameContext'
import { useR2Highlights } from '../../../../providers/r2Provider/R2Context'
import Tip from '../../../../components/tip/Tip'
import { nameFieldTypes } from '../../utils/fullNameCorrectionUtils'

const iconCss = css`
  background-color: ${colors.blue30};
  padding: 4px 8px;
  border-start-end-radius: 6px;
  border-end-end-radius: 6px;
  cursor: grab;
`

const SortableList = ({ fields, fieldType, nameField }) => {
  const [t] = useTranslation()
  const state = useFullNameStateContext()
  const dispatch = useFullNameDispatchContext()
  const h = useSlsHighlightsContext()
  const dropSpotText = t('full-name-correction.step3.drop-spot-text', 'DRAG NAME FIELDS HERE.')
  const draggedFieldAriaDescription = t(
    'full-name-correction.step3.dragged-field-aria-description',
    'Drag the name field to the correct label and order.'
  )

  const dropSpotStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%233ac' stroke-width='3' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
    height: '70px',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  }

  const handleChangeFieldValue = (newFieldValue, id) => {
    const updatedStepThree = cloneDeep(state.currentNameParts)
    const fieldInState = updatedStepThree[fieldType]?.fields?.find((field) => field.id === id)

    // The reducer filters out illegal characters, so we don't worry about that here
    fieldInState.userEnteredValue = newFieldValue

    // Set an error message in the new currentNameParts object that corresponds to the TextField that shows this invalid value
    if (newFieldValue !== filterIllegalReviewFieldCharacters(newFieldValue)) {
      fieldInState.errorMessage = t(
        'task-viewer.error.illegal-characters',
        "You can only enter ' - , . diacritic, and alphanumeric characters."
      )
    } else {
      fieldInState.errorMessage = ''
    }

    dispatch({ type: 'SET_CURRENT_NAME_PARTS', value: updatedStepThree })
  }

  return (
    <Droppable id={nameField.fieldType} data={{ fieldType: nameField.fieldType }}>
      <SortableContext items={fields || []} strategy={verticalListSortingStrategy}>
        <H2 size="H6">{`${nameField.label}:`}</H2>
        {fields?.map((field, index) => {
          return (
            <React.Fragment key={field.id}>
              <SortableItem
                id={field.id}
                data={{
                  fieldType,
                  draggableStyle: {
                    display: 'flex',
                    marginTop: '8px',
                    backgroundColor: colors.gray00,
                    borderRadius: '6px',
                    maxHeight: '48px',
                    touchAction: 'none',
                  },
                  childrenStyle: {
                    flexGrow: 1,
                    padding: '4px',
                  },
                }}
                dragHandle={
                  <div css={iconCss}>
                    <IconButton
                      aria-label={draggedFieldAriaDescription}
                      iconColor={colors.gray100}
                      Icon={ControlDrag}
                    />
                  </div>
                }
              >
                <TextField
                  aria-label={nameField.label}
                  label={false}
                  data-testid={`${nameField.fieldType}-${index}`}
                  errorMessage={field?.errorMessage}
                  placeholder={nameField.label}
                  value={field?.userEnteredValue}
                  onChange={(event) => handleChangeFieldValue(event.target.value, field.id)}
                  onFocus={() => {
                    h.hoverHighlights({ elementIds: [field.id] })
                  }}
                  onBlur={() => h.hoverHighlights({ elementIds: [] })}
                />
              </SortableItem>

              {field?.errorMessage && <Separator size="md" />}
            </React.Fragment>
          )
        })}
        {!fields?.length && (
          <>
            <Separator size="xs" />
            <div css={dropSpotStyle}>
              <BillboardText>{dropSpotText}</BillboardText>
            </div>
          </>
        )}
        <Separator size="xs" />
        <Divider marginY="xs" />
      </SortableContext>
    </Droppable>
  )
}

export const handleDragOver = ({ event, handleDispatchNewStepThree, updatedStepThree }) => {
  const { active, over } = event
  const activeItem = updatedStepThree?.[active?.data.current?.fieldType]?.fields?.find(
    (field) => field.id === active.id
  )

  if (
    nameFieldTypes.includes(over?.id) &&
    nameFieldTypes.includes(active?.data.current.fieldType) &&
    active.data.current.fieldType !== over?.id &&
    activeItem
  ) {
    const activeItemIndex = updatedStepThree[activeItem.fieldType]?.fields?.indexOf(activeItem)
    activeItem.fieldType = over.id
    // Check if updateStepThree has the appropriate fieldType child. If not, add it
    if (!updatedStepThree[activeItem.fieldType]) updatedStepThree[activeItem.fieldType] = { fields: [] }
    // Add the activeItem to the fields array of the correct fieldType, putting it at the top if we dragged it down and at the bottom if we dragged it up
    if (active.rect.current.initial.top - active.rect.current.translated.top < 0)
      updatedStepThree[activeItem.fieldType].fields.unshift(activeItem)
    else updatedStepThree[activeItem.fieldType].fields.push(activeItem)
    // Remove the activeItem from its former position
    updatedStepThree[active.data.current.fieldType].fields.splice(activeItemIndex, 1)
    // If the moved field was the last one in its fields array, delete that fieldType child from updatedStepThree
    if (!updatedStepThree[active.data.current.fieldType].fields.length)
      delete updatedStepThree[active.data.current.fieldType]
    handleDispatchNewStepThree(updatedStepThree)
  }
}

export const handleDragEnd = ({ event, handleDispatchNewStepThree, updatedStepThree }) => {
  const { active, over } = event

  if (over && active.id !== over.id) {
    // Get the item we're displacing
    const overItem = updatedStepThree[over.data.current.fieldType]?.fields?.find((field) => field.id === over.id)
    // If we're displacing another item, we need to update the item's position and its fieldType
    if (overItem) {
      // Get the item we're moving
      const activeItem = updatedStepThree[active.data.current.fieldType].fields?.find((field) => field.id === active.id)
      // Get the current position of the activeItem so we know where to remove it from in the array
      const activeItemRemovalIndex = updatedStepThree[activeItem.fieldType]?.fields?.indexOf(activeItem)
      // Get the position of the overItem so we know where to put the activeItem
      const overItemIndex = updatedStepThree[overItem.fieldType]?.fields?.indexOf(overItem)
      // Remove the activeItem from its former position
      updatedStepThree[active.data.current.fieldType].fields.splice(activeItemRemovalIndex, 1)
      // Insert the activeItem into the position of the overItem
      if (updatedStepThree[overItem.fieldType] && updatedStepThree[overItem.fieldType].fields)
        updatedStepThree[overItem.fieldType].fields.splice(overItemIndex, 0, activeItem)
      handleDispatchNewStepThree(updatedStepThree)
    }
  }
}

const ReviewStep3 = ({ currentTask, types }) => {
  const [t] = useTranslation()
  const state = useFullNameStateContext()
  const dispatch = useFullNameDispatchContext()
  const { displayHighlightsApi } = useR2Highlights()
  const [selectedTempChips, setSelectedTempChips] = useState([])
  const [lockedTempChips, setLockedTempChips] = useState([])
  const [selectedFields, setSelectedFields] = useState([])
  const [removedFields, setRemovedFields] = useState([])
  const nameFieldSelectorOverlay = useOverlay()
  const h = useSlsHighlightsContext()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const getNameFieldChipsElement = useMemo(() => {
    const uniqueFieldInfoTypes = Object.values(state.currentNameParts).reduce((acc, fieldGroup) => {
      fieldGroup.fields.forEach((field) => {
        if (!acc.find((f) => f.fieldType === field.fieldType)) acc.push({ id: field.id, fieldType: field.fieldType })
      })
      return acc
    }, [])
    const nameFieldChipsElement = { subElements: [] }
    uniqueFieldInfoTypes.forEach((fieldType) => {
      nameFieldChipsElement.subElements.push({ subElements: [fieldType] })
    })

    return nameFieldChipsElement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { selectedFieldData, treatedNameFieldChipData } = useTreatedNameFieldChips(
    getNameFieldChipsElement,
    types,
    selectedFields,
    selectedTempChips,
    lockedTempChips
  )

  const handleDispatchNewStepThree = debounce((updatedStepThree) => {
    dispatch({ type: 'SET_CURRENT_NAME_PARTS', value: updatedStepThree })
  }, 50)

  const handleHoverOnDrag = useCallback(
    (highlightIds) => {
      displayHighlightsApi.hover(highlightIds)
    },
    [displayHighlightsApi]
  )

  useEffect(() => {
    setSelectedFields(cloneDeep(selectedFieldData))
  }, [selectedFieldData])

  const handleNameFieldsChipSelection = (chips) => {
    const getFieldsToRemove = (field) => !chips.map(({ id }) => id).includes(field.id)
    const newFields = [...selectedFields]
    const updatedCurrentNameParts = cloneDeep(state.currentNameParts)
    const { mainEditHighlights } = h.getHighlights()
    const updatedEditHighlights = [...mainEditHighlights]
    let updatedRemovedFields = [...removedFields]

    const removeField = (field) => {
      if (updatedCurrentNameParts[field.fieldType]) {
        updatedCurrentNameParts[field.fieldType].fields.forEach((fieldInState) => {
          updatedEditHighlights.find((highlight) => highlight.id === fieldInState.id).attached = false
          displayHighlightsApi.remove(fieldInState.id)
          updatedRemovedFields.push(fieldInState)
        })
        delete updatedCurrentNameParts[field.fieldType]
      }
      const index = newFields.findIndex((f) => f.id === field.id)
      newFields.splice(index, 1)
    }

    selectedFields.filter(getFieldsToRemove).forEach(removeField)

    const newChips = chips.filter((chip) => !chip.id)
    if (newChips) {
      newChips.forEach((chip) => {
        const newField = {
          label: chip.label,
          fieldType: chip.value.split(':')[0],
          id: getNextUuid(),
          added: true,
        }
        newFields.push(newField)
        updatedRemovedFields
          .filter((removedField) => removedField.fieldType === newField.fieldType)
          .forEach((restoredField) => {
            updatedEditHighlights.find((highlight) => highlight.id === restoredField.id).attached = true
            displayHighlightsApi.add(
              restoredField.id,
              state.highlights.find((highlight) => highlight.id === restoredField.id)
            )
            if (!updatedCurrentNameParts[newField.fieldType]) {
              updatedCurrentNameParts[newField.fieldType] = { fields: [] }
            }
            updatedCurrentNameParts[newField.fieldType].fields.push(restoredField)
          })
        updatedRemovedFields = updatedRemovedFields.filter(
          (removedField) => removedField.fieldType !== newField.fieldType
        )
      })
    }

    setRemovedFields(updatedRemovedFields)
    dispatch({ type: 'SET_CURRENT_NAME_PARTS', value: updatedCurrentNameParts })

    // create an object in the shape of what sortPersonNameSubElements can sort
    const pseudoPerson = {
      subElements: [{ elementType: 'NAME', subElements: newFields }],
    }
    sortPersonNameSubElements(pseudoPerson)
    setSelectedFields(newFields)
  }

  const onDragStartHandler = useCallback(
    (event) => {
      const activeItem = state.currentNameParts[event.active.data.current.fieldType].fields.find(
        (field) => field.id === event.active.id
      )

      handleHoverOnDrag(activeItem.id)
    },
    [handleHoverOnDrag, state.currentNameParts]
  )

  const onDragOverHandler = useCallback(
    (event) => {
      const updatedStepThree = cloneDeep(state.currentNameParts)

      handleDragOver({
        event,
        handleDispatchNewStepThree,
        updatedStepThree,
        handleHoverOnDrag,
      })
    },
    [handleDispatchNewStepThree, handleHoverOnDrag, state.currentNameParts]
  )

  const onDragEndHandler = useCallback(
    (event) => {
      handleHoverOnDrag([])
      const updatedStepThree = cloneDeep(state.currentNameParts)
      handleDragEnd({
        event,
        handleDispatchNewStepThree,
        updatedStepThree,
      })
    },
    [handleDispatchNewStepThree, handleHoverOnDrag, state.currentNameParts]
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={onDragStartHandler}
      onDragOver={onDragOverHandler}
      onDragEnd={onDragEndHandler}
    >
      <H2 size="H5">
        {t(
          'full-name-correction.step3.heading',
          'Drag the name parts below via the blue drag icon to the correct label and order.'
        )}
      </H2>
      <Separator size="xs" />
      <Paragraph size="sm">
        {t('full-name-correction.step3.must-have-name-part', 'Must have a name part in Given Name or Surname.')}
      </Paragraph>
      <Tip
        primaryText={t('full-name-correction.step3.tip.heading', 'Why is there an empty text box?')}
        listPrimaryText={t(
          'full-name-correction.tip.main',
          'Each text box is connected to a single highlight. Click on the box to see which higlight is connected to what.'
        )}
      />
      <Tip
        primaryText={t('full-name-correction.step3.tip2.heading', 'Do I have to fill every text box?')}
        listPrimaryText={t(
          'full-name-correction.step3.tip2.main',
          'No, the text boxes are there to help you move the highlights to the right sections. As long as the text is in the right order and location, it will be combined in step four.'
        )}
      />
      <Separator />
      <Card backgroundColor={colors.blue03}>
        <>
          {selectedFields
            .sort(
              (a, b) =>
                expandedNameFieldsSortMap.find((sort) => sort.fieldType === a.fieldType)?.value -
                expandedNameFieldsSortMap.find((sort) => sort.fieldType === b.fieldType)?.value
            )
            .map((nameField) => (
              <SortableList
                fields={state.currentNameParts[nameField.fieldType]?.fields}
                fieldType={nameField.fieldType}
                nameField={nameField}
                key={nameField.fieldType}
              />
            ))}
        </>
        <BillboardButton
          data-testid="add-or-remove-fields-button"
          emphasis="low"
          Icon={ContentAdd}
          onClick={nameFieldSelectorOverlay.handleClick}
        >
          {t('edit-index.add-fields.button', 'Add or Remove Fields')}
        </BillboardButton>
      </Card>
      {state.noGivenOrSurnameError && (
        <>
          <Separator size="sm" />
          <Alert type="error">
            {t('full-name-correction.step3.must-have-name-part', 'Must have a name part in Given Name or Surname.')}
          </Alert>
        </>
      )}
      <FieldSelectorOverlay
        allowSelectingMultiple={false}
        title={t('edit-index.add-fields.title', 'Add or Remove Fields')}
        chipData={[
          {
            title: t('edit-index.chips.name-fields.title', 'Name'),
            data: treatedNameFieldChipData,
            key: `nameFields-${currentTask.id}`,
          },
        ]}
        fieldData={selectedFields}
        handleSelection={handleNameFieldsChipSelection}
        handleSelectionChange={(chips, lockedChips) => {
          setSelectedTempChips(chips)
          setLockedTempChips(lockedChips)
        }}
        overlay={nameFieldSelectorOverlay}
        disabledFunc={disableSaveFuncRequireGnOrSurn}
      />
    </DndContext>
  )
}

export default ReviewStep3
